import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import { HelpTooltip } from '@/kit';
import type { ByoidLevelOption, ExternalScopeLevelLocal } from '../../api/types/PlatformLocal';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';

// DEC-RBAC-027: el tenant puede traer su PROPIO id (BYOID — bring your own id) en ciertos niveles en vez
// de un UUID generado. El control es un grupo de checkboxes (mismo patrón que SecurityPolicyEditor — MFA):
// sin ninguno marcado = BYOID apagado para ese tenant (`[]`); uno o más marcados = prendido en esos
// niveles. No hay switch maestro aparte: el set vacío ya representa "apagado" (YAGNI).
//
// Los niveles que se renderizan salen de las `options` que arma el modal a partir de la APP del tenant
// (sus `levels`, mostrando el `displayName` real de cada nivel). Tras la ampliación del contrato
// (`externalScopeLevels: PermissionScope[]` incluye `LEVEL_1/2/3`), todos los niveles de una app son
// persistibles (su `byoidValue` viene definido) → checkbox habilitado y marcable.

interface Props {
    /** Opciones a renderizar (label + valor persistible), en orden canónico. Las arma el modal. */
    options: ByoidLevelOption[];
    /** Niveles BYOID actualmente prendidos (ya intersectado con los `options` persistibles). */
    value: ExternalScopeLevelLocal[];
    /** Notifica el nuevo set completo al marcar/desmarcar un nivel. */
    onChange: (next: ExternalScopeLevelLocal[]) => void;
    /** Deshabilita los checkboxes (gating por permiso — mismo `canEdit` que el resto del modal). */
    disabled?: boolean;
}

export function ExternalScopeLevelsEditor({ options, value, onChange, disabled }: Props) {
    const { t } = useTranslation('tenant');

    // Orden canónico de persistencia = el orden de los `byoidValue` presentes en `options`.
    const persistableOrder: ExternalScopeLevelLocal[] = options
        .map((o) => o.byoidValue)
        .filter((v): v is ExternalScopeLevelLocal => v !== undefined);

    const toggle = (level: ExternalScopeLevelLocal, checked: boolean): void => {
        // Mantiene el orden canónico, sin duplicados ni niveles fuera del set persistible.
        const next = checked
            ? persistableOrder.filter((l) => value.includes(l) || l === level)
            : value.filter((l) => l !== level);
        onChange(next);
    };

    return (
        <fieldset className="space-y-4">
            <legend className="text-sm font-medium">
                <span className="inline-flex items-center gap-1">
                    {t('externalIds.title')}
                    <HelpTooltip content="Permite que los usuarios traigan su propio ID externo (BYOID) en estos niveles, en vez de un ID generado." />
                </span>
            </legend>
            <p className="flex items-start gap-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                <KeyRound className="mt-0.5 size-4 shrink-0 opacity-70" />
                <span>{t('externalIds.hint')}</span>
            </p>
            {options.map((opt) => {
                // Todos los niveles de una app son persistibles (su `byoidValue` viene definido); el guard
                // por `undefined` queda como narrowing defensivo del tipo opcional de `ByoidLevelOption`.
                const byoidValue = opt.byoidValue;
                const checked = byoidValue !== undefined && value.includes(byoidValue);
                return (
                    <div key={opt.key} className="flex items-start gap-3">
                        <Checkbox
                            id={`byoid-${opt.key}`}
                            checked={checked}
                            disabled={disabled || byoidValue === undefined}
                            onCheckedChange={(c) => {
                                if (byoidValue !== undefined) toggle(byoidValue, c === true);
                            }}
                            className="mt-0.5"
                        />
                        <div className="space-y-0.5">
                            <Label htmlFor={`byoid-${opt.key}`} className="cursor-pointer">
                                {opt.label}
                            </Label>
                        </div>
                    </div>
                );
            })}
        </fieldset>
    );
}
